import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute, AdminRoute } from './components/ProtectedRoute';
import AuthCallback from './components/AuthCallback';
import AppErrorBoundary from './components/AppErrorBoundary';
import { Toaster } from 'sonner';
import { MiniMusicPlayer } from './components/MiniMusicPlayer';

import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import BecomeAPartner from './pages/BecomeAPartner';
import Dashboard from './pages/Dashboard';
import CreateMemorial from './pages/CreateMemorial';
import MemorialAdmin from './pages/MemorialAdmin';
import InteractiveBook from './pages/InteractiveBook';
import PublicMemorial from './pages/PublicMemorial';
import LeaveMemory from './pages/LeaveMemory';
import VaultAccess from './pages/VaultAccess';
import BillingSuccess from './pages/BillingSuccess';
import FindFuneralHomes from './pages/FindFuneralHomes';
import ProfessionalDashboard from './pages/ProfessionalDashboard';
import AdminProfessionalApplications from './pages/AdminProfessionalApplications';
import PartnerDashboard from './pages/PartnerDashboard';
import AdminPartnerApplications from './pages/AdminPartnerApplications';
import AdminOverrideContracts from './pages/AdminOverrideContracts';
import AdminFamilies from './pages/AdminFamilies';
import AdminReports from './pages/AdminReports';import RequestAssistance from './pages/RequestAssistance';
import PartnerOnboardingComplete from './pages/PartnerOnboardingComplete';
import AdminPromoCodes from './pages/AdminPromoCodes';
import AdminAssistanceRequests from './pages/AdminAssistanceRequests';
import AdminEngravingQuotes from './pages/AdminEngravingQuotes';
import AdminPlaqueOrders from './pages/AdminPlaqueOrders';
import AdminProducts from './pages/AdminProducts';
import AdminAccounts from './pages/AdminAccounts';
import AdminLiveCleanup from './pages/AdminLiveCleanup';
import AdminAccountDetail from './pages/AdminAccountDetail';
import SignAgreement from './pages/SignAgreement';
import AdminMemorials from './pages/AdminMemorials';
import AdminMemorialDetail from './pages/AdminMemorialDetail';
import AdminCommissions from './pages/AdminCommissions';
import AdminBillingHealth from './pages/AdminBillingHealth';
import PlaqueOrder from './pages/PlaqueOrder';
import PlaqueOrderSuccess from './pages/PlaqueOrderSuccess';
import ForgotPassword from './pages/ForgotPassword';
import AccountSettings from './pages/AccountSettings';
import ResetPassword from './pages/ResetPassword';
import NotFound from './pages/NotFound';
import { captureReferralParams } from './lib/referral';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// iter101 — Referral attribution capture on EVERY page. A partner link can
// land anywhere (homepage, pricing, a memorial) and the code survives
// refreshes and navigation until signup (30-day window, last-touch wins).
function ReferralCapture() {
  const { search } = useLocation();
  useEffect(() => { captureReferralParams(search); }, [search]);
  return null;
}

function RouteSwitch() {
  return (
    <>
      <ScrollToTop />
      <ReferralCapture />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/become-a-partner" element={<BecomeAPartner />} />
        <Route path="/partners/apply" element={<Navigate to="/become-a-partner" replace />} />
        <Route path="/become-partner" element={<Navigate to="/become-a-partner" replace />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth/google/callback" element={<AuthCallback />} />

        <Route path="/m/:slug" element={<PublicMemorial />} />
        <Route path="/m/:slug/memory" element={<LeaveMemory />} />
        <Route path="/vault/:token" element={<VaultAccess />} />
        <Route path="/sign-agreement/:token" element={<SignAgreement />} />
        <Route path="/find-funeral-homes" element={<FindFuneralHomes />} />
        <Route path="/request-assistance" element={<RequestAssistance />} />
        <Route path="/billing/success" element={<ProtectedRoute><BillingSuccess /></ProtectedRoute>} />

        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/account/settings" element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />
        <Route path="/account" element={<Navigate to="/account/settings" replace />} />
        <Route path="/dashboard/new" element={<ProtectedRoute><CreateMemorial /></ProtectedRoute>} />
        <Route path="/dashboard/m/:id" element={<ProtectedRoute><MemorialAdmin /></ProtectedRoute>} />
        <Route path="/dashboard/m/:id/*" element={<ProtectedRoute><MemorialAdmin /></ProtectedRoute>} />
        <Route path="/m/:slug/book" element={<InteractiveBook />} />
        <Route path="/professional" element={<ProtectedRoute><ProfessionalDashboard /></ProtectedRoute>} />
        <Route path="/admin/professional/applications" element={<AdminRoute><AdminProfessionalApplications /></AdminRoute>} />

        {/* New partner hierarchy (Phase 1) */}
        <Route path="/partners" element={<ProtectedRoute><PartnerDashboard /></ProtectedRoute>} />
        <Route path="/partners/regional" element={<ProtectedRoute><PartnerDashboard /></ProtectedRoute>} />
        <Route path="/partners/memorial" element={<ProtectedRoute><PartnerDashboard /></ProtectedRoute>} />
        <Route path="/admin/partners" element={<AdminRoute><AdminPartnerApplications /></AdminRoute>} />
        <Route path="/admin/partners/applications" element={<AdminRoute><AdminPartnerApplications /></AdminRoute>} />
        <Route path="/admin/partners/overrides" element={<AdminRoute><AdminOverrideContracts /></AdminRoute>} />
        <Route path="/admin/families" element={<AdminRoute><AdminFamilies /></AdminRoute>} />
        <Route path="/admin/reports" element={<AdminRoute><AdminReports /></AdminRoute>} />
        <Route path="/partners/onboarding/complete" element={<ProtectedRoute><PartnerOnboardingComplete /></ProtectedRoute>} />
        <Route path="/partners/onboarding/refresh" element={<ProtectedRoute><PartnerOnboardingComplete /></ProtectedRoute>} />

        {/* Admin tools (Phase 3) */}
        <Route path="/admin/promo-codes" element={<AdminRoute><AdminPromoCodes /></AdminRoute>} />
        <Route path="/admin/assistance" element={<AdminRoute><AdminAssistanceRequests /></AdminRoute>} />
        <Route path="/admin/assistance/requests" element={<AdminRoute><AdminAssistanceRequests /></AdminRoute>} />
        <Route path="/admin/engraving-quotes" element={<AdminRoute><AdminEngravingQuotes /></AdminRoute>} />
        <Route path="/admin/plaque-orders" element={<AdminRoute><AdminPlaqueOrders /></AdminRoute>} />
        <Route path="/admin/products" element={<AdminRoute><AdminProducts /></AdminRoute>} />
        <Route path="/admin/accounts" element={<AdminRoute><AdminAccounts /></AdminRoute>} />
        <Route path="/admin/live-cleanup" element={<AdminRoute><AdminLiveCleanup /></AdminRoute>} />
        <Route path="/admin/commissions" element={<AdminRoute><AdminCommissions /></AdminRoute>} />
        <Route path="/admin/billing-health" element={<AdminRoute><AdminBillingHealth /></AdminRoute>} />
        <Route path="/admin/accounts/:userId" element={<AdminRoute><AdminAccountDetail /></AdminRoute>} />
        <Route path="/admin/memorials" element={<AdminRoute><AdminMemorials /></AdminRoute>} />
        <Route path="/admin/memorials/:memorialId" element={<AdminRoute><AdminMemorialDetail /></AdminRoute>} />
        <Route path="/memorials/:memorialId/order-plaque" element={<ProtectedRoute><PlaqueOrder /></ProtectedRoute>} />
        <Route path="/plaque-order/success" element={<ProtectedRoute><PlaqueOrderSuccess /></ProtectedRoute>} />
        <Route path="/plaque-order/cancelled" element={<ProtectedRoute><PlaqueOrderSuccess /></ProtectedRoute>} />

        {/* Legacy paths (removed iter42) — redirect quietly so any bookmarks
            or share links land somewhere coherent instead of a console
            warning. */}
        <Route path="/partner" element={<Navigate to="/professional" replace />} />
        <Route path="/admin/commissions" element={<Navigate to="/admin/professional/applications" replace />} />

        {/* Any other path → calm 404 page. */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <AppErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <RouteSwitch />
          {/* Persistent music preview — only visible while a soundtrack is playing.
              Mounted globally so it survives tab switches and route changes. */}
          <MiniMusicPlayer />
          <Toaster
            position="bottom-right"
            toastOptions={{
              style: {
                background: '#1A3626',
                color: '#FDFBF7',
                border: '1px solid #C5A059',
                fontFamily: 'Manrope, sans-serif',
              },
            }}
          />
        </BrowserRouter>
      </AuthProvider>
    </AppErrorBoundary>
  );
}
